import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../constants";
import { useClub } from "./ClubContext";
import { useSeason } from "./SeasonContext";
import { Game } from "../types/ScheduleTypes";
import { Club } from "../types/ClubTypes";
import { Standing } from "../types/ScheduleTypes";

interface ScheduleContextType {
  games: Game[];
  standings: Standing[];
  clubs: Club[];
  isLoading: boolean;
  error: string | null;
  fetchGames: (clubId: number) => Promise<void>;
  fetchStandings: (season: string) => Promise<void>;
  fetchClubs: () => Promise<void>;
}

const ScheduleContext = createContext<ScheduleContextType | undefined>(
  undefined
);

export function ScheduleProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);

  const { club } = useClub();
  const { selectedSeason } = useSeason();

  useEffect(() => {
    if (club) {
      fetchStandings(selectedSeason?.name || "2024");
      fetchGames(club.id);
      fetchClubs();
    }
  }, [club, selectedSeason]);

  const fetchGames = async (clubId: number) => {
    if (!club) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/clubs/schedule/`, {
        params: {
          season: selectedSeason?.name,
          club_id: clubId,
        },
      });
      setGames(response.data);
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching games");
      console.error("Error fetching games:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStandings = async (season: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/clubs/standings/`, {
        params: { season: season },
      });
      setStandings(response.data);
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching standings");
      console.error("Error fetching standings:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clubs/`);
      setClubs(response.data);
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching clubs");
      console.error("Error fetching clubs:", err);
    }
  };

  const value = {
    games,
    standings,
    clubs,
    isLoading,
    error,
    fetchGames,
    fetchStandings,
    fetchClubs,
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
}

export function useSchedule() {
  const context = useContext(ScheduleContext);
  if (context === undefined) {
    throw new Error("useSchedule must be used within a ScheduleProvider");
  }
  return context;
}
